import { clsx } from "clsx";
import { extendTailwindMerge, twMerge } from "tailwind-merge";
export * from "./time";
export * from "./playerImage";
export * from "./sbcListControls";
export * from "./solveSettings";
export * from "./solutionFacts";
export * from "./playerRoles";
export * from "./chemStyles";
export * from "./chemStyleAccelerate";
export * from "./chemStyleBonuses";
export * from "./offBalanceDribbling";
export * from "./playStyles";
export * from "./playerAttributes";
export * from "./filters";
export * from "./measurements";
export * from "./squadMetaRating";
/*
  tailwind-merge resolves `text-*` against its own list of font sizes and calls
  anything it does not recognise a colour. Our extra steps are not on that list,
  so `cn("text-md", "text-foreground-muted")` used to drop the size as the loser
  of a colour-vs-colour conflict — `md` is mobile's 16px step, and `text-md` sits
  in `Txt`'s own base classes, so every caller passing a colour silently lost it.
  Naming them here puts them back in the size group, where a size overrides a
  size and leaves the colour alone.
*/
const customTwMerge = extendTailwindMerge({
    extend: { classGroups: { "font-size": [{ text: ["tiny", "md"] }] } },
});
export function cn(...inputs) {
    return customTwMerge(clsx(inputs));
}
export const getPriceType = (price, sbcPrice, isSbcPlayer, isObjectivePlayer) => {
    if (price > 0) {
        return "market";
    }
    else if (sbcPrice > 0 || isSbcPlayer) {
        return "sbc";
    }
    else if (isObjectivePlayer) {
        return "xp";
    }
    return "market";
};
export function capitalizeWords(str) {
    if (!str)
        return "";
    const words = str?.split(" ");
    const capitalizedWords = words.map((word) => {
        if (word.length === 0) {
            return word;
        }
        const lowercaseWord = word.toLowerCase();
        return lowercaseWord.charAt(0).toUpperCase() + lowercaseWord.slice(1);
    });
    return capitalizedWords.join(" ");
}
export function capitalizeWordsAndRemoveDashes(str) {
    if (!str)
        return "";
    return capitalizeWords(str.replaceAll("-", " "));
}
export function URLizeString(str) {
    if (!str)
        return "";
    return normalizeEaString(str)
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "")
        .replace(/-+/g, "-")
        .replace(/^-+|-+$/g, "");
}
const contentSlug = ({ slug, name }) => slug || URLizeString(name);
export const sbcSetPath = (set) => `/sbc/${contentSlug(set)}/${set.id}`;
/* Groups carry no English slug, so the segment follows the name; routing keys on the id. */
export const objectiveGroupPath = (group) => `/objectives/${contentSlug(group)}/${group.id}`;
export const sbcPath = (set, sbc) => `${sbcSetPath(set)}/${contentSlug(sbc)}/${sbc.id}`;
export const evolutionOverviewPath = (evo) => `/evolutions/overview/${contentSlug(evo)}/${evo.id}`;
export function convertPlayerPrice(price, isMobile) {
    if (price === Number.MAX_VALUE) {
        return "";
    }
    let convertedPrice = String(price);
    if (!isMobile) {
        convertedPrice = price?.toLocaleString();
    }
    else {
        if (price < 1000) {
            convertedPrice = price?.toLocaleString();
        }
        else if (price < 100000) {
            convertedPrice = parseFloat((price / 1000).toFixed(1)) + "K";
        }
        else if (price < 1000000) {
            convertedPrice = parseFloat(String((price / 1000).toFixed(1))) + "K";
        }
        else {
            convertedPrice = parseFloat(String((price / 1000000).toFixed(1))) + "M";
        }
    }
    return convertedPrice;
}
export function formatPrice({ price, compact = false, }) {
    if (price === Number.MAX_VALUE)
        return "";
    // No price is not a price of zero: the market has no quote for this card.
    if (!price)
        return "-";
    if (!compact)
        return price.toLocaleString();
    const format = (value, suffix) => {
        const abs = Math.abs(value);
        let decimals;
        if (abs < 10)
            decimals = 2;
        else if (abs < 100)
            decimals = 1;
        else
            decimals = 0;
        const factor = Math.pow(10, decimals);
        const rounded = Math.round(value * factor) / factor;
        // parseFloat drops trailing zeros: "12K", not "12.0K".
        return parseFloat(rounded.toFixed(decimals)) + suffix;
    };
    if (price < 1000)
        return String(price);
    if (price < 1_000_000)
        return format(price / 1000, "K");
    return format(price / 1_000_000, "M");
}
/**
 * A coin total, as opposed to one card's quote.
 *
 * `formatPrice` renders 0 as "-", meaning "the market has no quote for this
 * card". A squad the club covers outright really does cost zero, so a total
 * says so rather than borrowing the no-quote dash.
 */
export function formatCoinTotal(price) {
    return price ? formatPrice({ price }) : "0";
}
/** "1.5K", "13K", "215K", "1.2M": one decimal under 10K, whole thousands above. */
export function convertSBCPrice(price) {
    if (!price)
        return "0";
    if (price > 999999)
        return parseFloat((price / 1000000).toFixed(2)) + "M";
    if (price > 9999)
        return (price / 1000).toFixed(0) + "K";
    if (price > 999)
        return parseFloat((price / 1000).toFixed(1)) + "K";
    return price.toString();
}
// ============ Time Breakdown Helpers ============
const EA_STRING_EQUALITY_LOOKUP = [
    { equivalent: "a", characters: "àáâãäåāăą" },
    { equivalent: "ae", characters: "æ" },
    { equivalent: "c", characters: "çćĉċč" },
    { equivalent: "d", characters: "ďđ" },
    { equivalent: "e", characters: "èéêëēĕėęě" },
    { equivalent: "g", characters: "ĝğġģ" },
    { equivalent: "h", characters: "ĥ" },
    { equivalent: "i", characters: "ìíîïĩīĭįı" },
    { equivalent: "j", characters: "ĵ" },
    { equivalent: "k", characters: "ķ" },
    { equivalent: "l", characters: "ĺļľŀł" },
    { equivalent: "n", characters: "ñńņňŉ" },
    { equivalent: "o", characters: "òóôõöōŏőø" },
    { equivalent: "r", characters: "ŕŗř" },
    { equivalent: "s", characters: "śŝşšș" },
    { equivalent: "t", characters: "ţťŧț" },
    { equivalent: "u", characters: "ùúûüũūŭůűų" },
    { equivalent: "y", characters: "ýŷ" },
    { equivalent: "z", characters: "źżž" },
];
const SPECIAL_CHAR_TO_NORMAL_CHAR_LOOKUP = EA_STRING_EQUALITY_LOOKUP.reduce((acc, curr) => {
    curr.characters.split("").forEach((char) => {
        acc[char] = curr.equivalent;
        acc[char.toLocaleUpperCase()] = curr.equivalent.toLocaleUpperCase();
    });
    return acc;
}, {});
export function normalizeEaString(str) {
    return str
        .split("")
        .map((char) => SPECIAL_CHAR_TO_NORMAL_CHAR_LOOKUP[char] ?? char)
        .join("");
}
export * from "./assets";
export * from "./objectiveReward";
export * from "./resultCount";
export * from "./list";
export * from "./priceDisplay";
export * from "./formationPositions";
//# sourceMappingURL=index.js.map